import { useEffect, useState } from "react";
import { Lock, Unlock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { NumPad } from "@/components/NumPad";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { enterFullscreen, relockKiosk, unlockKiosk, useKiosk } from "@/lib/kiosk";
import { useUpdateAvailable } from "@/lib/updates";

export function KioskPinDialog({ open, onClose, onOk }) {
  const { t, view, verifyPin } = useApp();
  const [pin, setPin] = useState("");
  const [err, setErr] = useState(false);
  const owners = (view.staff || []).filter((s) => s.active && (s.role === "owner" || s.role === "manager"));
  useEffect(() => {
    if (pin.length !== 4) return;
    Promise.all(owners.map((m) => verifyPin(m, pin))).then((rs) => {
      if (rs.some(Boolean)) { setPin(""); onOk(); } else { setErr(true); setTimeout(() => { setErr(false); setPin(""); }, 700); }
    });
  }, [pin]); // eslint-disable-line
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="kiosk-pin-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Lock className="w-5 h-5" />{t("kiosk_exit_title")}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{t("kiosk_exit_desc")}</p>
        <div className={`flex justify-center gap-4 my-2 ${err ? "shake" : ""}`} dir="ltr" data-testid="kiosk-pin-dots">{[0, 1, 2, 3].map((i) => <span key={i} className={`w-4 h-4 rounded-full border-2 ${i < pin.length ? "bg-[#1F4E8C] border-[#1F4E8C]" : "border-[#999]"}`} />)}</div>
        {err && <p className="text-center text-destructive font-bold" data-testid="kiosk-pin-error">{t("pin_wrong")}</p>}
        <NumPad value={pin} onChange={(v) => setPin(v.slice(0, 4))} testid="kiosk-pin-digit" allowDot={false} />
      </DialogContent>
    </Dialog>
  );
}

export function KioskButton({ compact = false }) {
  const { t } = useApp();
  const { kiosk, locked } = useKiosk();
  const [ask, setAsk] = useState(false);
  if (!kiosk) return null;
  const cls = compact ? "h-6 px-2 rounded text-[11px] font-bold flex items-center gap-1" : "h-11 px-4 rounded-xl text-sm font-bold flex items-center gap-2";
  return (
    <>
      {locked
        ? <button type="button" onClick={() => setAsk(true)} className={`${cls} bg-[#C7353B] text-white`} data-testid="kiosk-exit-btn"><Lock className="w-3.5 h-3.5" />{t("kiosk_exit")}</button>
        : <button type="button" onClick={relockKiosk} className={`${cls} bg-[#1F9D55] text-white`} data-testid="kiosk-relock-btn"><Unlock className="w-3.5 h-3.5" />{t("kiosk_relock")}</button>}
      <KioskPinDialog open={ask} onClose={() => setAsk(false)} onOk={() => { setAsk(false); unlockKiosk(); toast.success(t("kiosk_unlocked")); }} />
    </>
  );
}

export function KioskGuard() {
  const { t } = useApp();
  const { locked } = useKiosk();
  const [ask, setAsk] = useState(false);
  useEffect(() => {
    if (!locked) return;
    const go = () => enterFullscreen();
    const onFs = () => { if (!document.fullscreenElement) setAsk(true); };
    const onUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    const onKey = (e) => { if (e.key === "F11" || (e.altKey && e.key === "F4")) { e.preventDefault(); e.stopPropagation(); } };
    document.addEventListener("pointerdown", go, true);
    document.addEventListener("fullscreenchange", onFs);
    window.addEventListener("beforeunload", onUnload);
    window.addEventListener("keydown", onKey, true);
    go();
    return () => { document.removeEventListener("pointerdown", go, true); document.removeEventListener("fullscreenchange", onFs); window.removeEventListener("beforeunload", onUnload); window.removeEventListener("keydown", onKey, true); };
  }, [locked]);
  if (!locked) return null;
  return (
    <>
      <div className="fixed top-0 inset-x-0 h-[3px] bg-[#C7353B] z-[60] pointer-events-none" data-testid="kiosk-indicator" title={t("kiosk_on")} />
      <KioskPinDialog open={ask} onClose={() => { setAsk(false); enterFullscreen(); }} onOk={() => { setAsk(false); unlockKiosk(); toast.success(t("kiosk_unlocked")); }} />
    </>
  );
}

export function UpdateBanner() {
  const { t } = useApp();
  const ready = useUpdateAvailable();
  const [hide, setHide] = useState(false);
  if (!ready || hide || window.location.pathname.startsWith("/r")) return null;
  return (
    <div className="fixed bottom-4 inset-x-0 z-[70] flex justify-center px-3 pointer-events-none" data-testid="update-banner">
      <div className="pointer-events-auto rounded-2xl bg-[#0B2A5B] text-white shadow-2xl px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
        <RefreshCw className="w-5 h-5 text-[#F28C28]" />
        <span className="font-semibold">{t("update_ready")}</span>
        <button type="button" onClick={() => window.location.reload()} className="h-9 px-4 rounded-xl bg-[#F28C28] text-[#1A1A1A] font-bold" data-testid="update-reload-btn">{t("update_reload")}</button>
        <button type="button" onClick={() => setHide(true)} className="h-9 px-3 rounded-xl border border-white/30 font-semibold" data-testid="update-later-btn">{t("update_later")}</button>
      </div>
    </div>
  );
}
